import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { LoginCallback } from '@okta/okta-react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  GlobalStyles,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Snackbar,
  Checkbox,
  List,
  ListItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  AccountTree,
  Refresh,
  Close,
  Logout,
  ExpandMore,
  ExpandLess,
  FileDownload,
  Apps,
  AccountBalance,
  Security,
  Edit,
  CloudQueue,
  Assessment,
  FilterList,
  DarkMode,
  LightMode
} from '@mui/icons-material';
import AccountDetailPage from './components/AccountDetailPage';
import DeletedAccountsDialog from './components/DeletedAccountsDialog';
import TagsManagement from './components/TagsManagement';
import BulkApproversManagement from './components/BulkApproversManagement';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import FunctionalUnitDistribution from './components/FunctionalUnitDistribution';
import useSessionTimeout from './hooks/useSessionTimeout';
import { useIsAdmin } from './hooks/useIsAdmin';
import { useIsEditor } from './hooks/useIsEditor';
import { useIsGovCloudReader } from './hooks/useIsGovCloudReader';
import packageJson from '../package.json';
const APP_VERSION = packageJson.version;

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('DataGrid Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          <Typography variant="h6">Something went wrong with the data display</Typography>
          <Typography variant="body2">Please try refreshing the page or clearing filters.</Typography>
          <Button 
            onClick={() => this.setState({ hasError: false, error: null })} 
            sx={{ mt: 1 }}
            variant="outlined"
            size="small"
          >
            Try Again
          </Button>
        </Alert>
      );
    }

    return this.props.children;
  }
}

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://your-api-gateway-url/prod';
const GOVCLOUD_API_ENDPOINT = process.env.REACT_APP_GOVCLOUD_API_ENDPOINT || '';

const getSystemDarkMode = () =>
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

// Context for dark mode + partition switching
const ColorModeContext = React.createContext({
  darkMode: false,
  toggleDarkMode: () => {},
  partition: 'commercial',
  setPartition: () => {},
});

const drawerWidth = 320;

// Decommission states — mirrors the backend `decom_state` field
const DECOM_STATES = {
  DELETED: { label: 'Deleted', color: 'error' },
  DIVESTITURE: { label: 'Divestiture', color: 'warning' },
  PENDING_DELETION: { label: 'Pending Deletion', color: 'info' },
};

const DECOM_FILTERS = ['ALL', 'DELETED', 'DIVESTITURE', 'PENDING_DELETION'];

// Mirrors backend _derive_decom_state so the view still renders correctly if a
// record predates the backend change and carries no decom_state field.
const getDecomState = (record) => {
  if (record?.decom_state && DECOM_STATES[record.decom_state]) return record.decom_state;
  const status = String(record?.account_status || '').trim().toUpperCase();
  return (status === 'DELETED' || status === 'DIVESTITURE') ? status : 'PENDING_DELETION';
};

const Login = () => {
  const { oktaAuth } = useOktaAuth();
  
  useEffect(() => {
    oktaAuth.signInWithRedirect();
  }, [oktaAuth]);
  
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Redirecting to Okta...
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we redirect you to sign in.
        </Typography>
      </Paper>
    </Container>
  );
};

const Dashboard = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const isAdmin = useIsAdmin();
  const isEditor = useIsEditor();
  const canEdit = isAdmin || isEditor;
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode, partition, setPartition } = React.useContext(ColorModeContext);
  const isGovCloudReader = useIsGovCloudReader();
  const [allAccounts, setAllAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [allOUs, setAllOUs] = useState([]);
  const [accountCounts, setAccountCounts] = useState({ total: 0, active: 0, suspended: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOU, setSelectedOU] = useState('');
  const [currentOU, setCurrentOU] = useState(null);
  const [tagsManagementOpen, setTagsManagementOpen] = useState(false);
  const [bulkApproversOpen, setBulkApproversOpen] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  
  // Functional Unit Distribution state
  const [functionalUnitStats, setFunctionalUnitStats] = useState({ functional_units: [], total_accounts: 0 });
  const [functionalUnitStatsLoading, setFunctionalUnitStatsLoading] = useState(false);
  const [functionalUnitStatsError, setFunctionalUnitStatsError] = useState(null);

  // Session timeout management
  const { 
    showWarning: showSessionWarning, 
    timeRemaining, 
    extendSession, 
    logout: sessionLogout 
  } = useSessionTimeout({
    idleTimeout: 30 * 60 * 1000,      // 30 minutes idle
    sessionTimeout: 8 * 60 * 60 * 1000, // 8 hours total
    warningTime: 5 * 60 * 1000        // 5 minutes warning
  });

  const handleRowClick = (account) => {
    const accountIds = filteredAccounts.map(a => a.id);
    const currentIndex = accountIds.indexOf(account.id);
    navigate('/accounts/' + account.id, { state: { accountIds, currentIndex, partition } });
  };

  const exportToCSV = async (exportAll = false) => {
    try {
      // Show loading state
      setLoading(true);
      
      // Determine which accounts to export based on selection
      let accountsToExport;
      let exportType;
      
      if (selectedAccounts.length > 0) {
        // If accounts are selected, export only selected
        accountsToExport = selectedAccounts;
        exportType = 'selected';
      } else if (exportAll) {
        // Export all accounts
        accountsToExport = allAccounts;
        exportType = 'all';
      } else {
        // Export filtered results
        accountsToExport = filteredAccounts;
        exportType = 'filtered';
      }
      
      console.log(`Exporting ${exportType} accounts: ${accountsToExport.length} accounts`);

      const headers = [
        'Account Name', 'Account ID', 'Email', 'Status', 'OU Path',
        'Joined Method', 'Joined Date', 'Functional Unit', 'Business Unit',
        'Account Owner', 'Technical Contact', 'Cloud Access Approvers',
        'Forwarding DL Name', 'Forwarding DL Address', 'DL Members',
      ];

      // Fetch approvers for all accounts that don't have them
      const accountsWithApprovers = await Promise.all(
        accountsToExport.map(async (account) => {
          if (!account.approvers) {
            try {
              const response = await authenticatedFetch(`${ACTIVE_API}/accounts/${account.id}/approvers`);
              const data = await response.json();
              return { ...account, approvers: data.approvers || [] };
            } catch (err) {
              console.error(`Error fetching approvers for ${account.id}:`, err);
              return { ...account, approvers: [] };
            }
          }
          return account;
        })
      );

      // Fetch Exchange info for all accounts
      const exchangeMap = {};
      const batchSize = 10;
      for (let i = 0; i < accountsWithApprovers.length; i += batchSize) {
        const batch = accountsWithApprovers.slice(i, i + batchSize);
        await Promise.all(batch.map(async (account) => {
          try {
            const response = await authenticatedFetch(
              `${ACTIVE_API}/accounts/${account.id}/exchange-info?cache_only=true`
            );
            const data = await response.json();
            exchangeMap[account.id] = data.success ? data : null;
          } catch (err) {
            exchangeMap[account.id] = null;
          }
        }));
      }
      const accountsWithExchange = accountsWithApprovers.map(account => ({
        ...account,
        exchangeInfo: exchangeMap[account.id] || null
      }));
      
      const csvData = accountsWithExchange.map(account => {
        const exo = account.exchangeInfo;
        const forwardingTarget = exo?.forwardingTarget;
        const dlMembers = exo?.dlMembers || [];
        return [
          account.name,
          account.id,
          account.email,
          account.status,
          account.ouPath || account.ouName || '',
          account.joinedMethod || '',
          account.joinedTimestamp ? new Date(account.joinedTimestamp).toLocaleDateString() : '',
          account.tags?.functionalUnit || '',
          account.tags?.businessUnit || '',
          account.tags?.accountOwner || '',
          account.tags?.technicalContact ? account.tags.technicalContact.replace(/\n/g, '; ') : '',
          account.approvers && account.approvers.length > 0
            ? account.approvers.map(approver => approver.name || approver).join('; ')
            : '',
          // Exchange columns
          forwardingTarget?.DisplayName || '',
          forwardingTarget?.PrimarySmtpAddress || '',
          dlMembers.length > 0 ? dlMembers.map(m => m.DisplayName || m.PrimarySmtpAddress).join('; ') : '',
        ];
      });
      
      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `verisk-aws-accounts-${exportType}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const [functionalUnitFilter, setFunctionalUnitFilter] = useState('');
  const [businessUnitFilter, setBusinessUnitFilter] = useState('');
  const [accountOwnerFilter, setAccountOwnerFilter] = useState('');
  const [technicalContactFilter, setTechnicalContactFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [clearCacheDialogOpen, setClearCacheDialogOpen] = useState(false);
  const [appDrawerOpen, setAppDrawerOpen] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copySnackbarMessage, setCopySnackbarMessage] = useState('');
  const [govCloudDeniedOpen, setGovCloudDeniedOpen] = useState(false);

  // Deleted accounts state (admin-only)
  const [deletedAccountsCount, setDeletedAccountsCount] = useState(null);
  const [deletedAccountsLoading, setDeletedAccountsLoading] = useState(false);
  const [deletedAccounts, setDeletedAccounts] = useState(null);
  const [showDeletedView, setShowDeletedView] = useState(false);
  const [decomFilter, setDecomFilter] = useState('ALL'); // ALL | DELETED | DIVESTITURE | PENDING_DELETION
  const [deletedAccountDetail, setDeletedAccountDetail] = useState(null); // row clicked → show full record

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'select',
        headerName: '',
        width: 50,
        sortable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Checkbox
            checked={selectedAccounts.length === filteredAccounts.length && filteredAccounts.length > 0}
            indeterminate={selectedAccounts.length > 0 && selectedAccounts.length < filteredAccounts.length}
            onChange={handleSelectAll}
          />
        ),
        renderCell: (params) => (
          <Checkbox
            checked={selectedAccounts.some(acc => acc.id === params.row.id)}
            onChange={(e) => {
              e.stopPropagation();
              handleSelectAccount(params.row);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )
      },
      { field: 'name', headerName: 'Account Name', flex: 1, minWidth: 150, sortable: true },
      { field: 'id', headerName: 'Account ID', width: 120, sortable: true },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 180, sortable: true },
      { 
        field: 'status', 
        headerName: 'Status', 
        width: 100, 
        sortable: true,
        renderCell: (params) => (
          <Chip 
            label={params.value || 'Unknown'}
            color={params.value === 'ACTIVE' ? 'success' : 'error'}
            size="small"
          />
        )
      },
      { field: 'ouPath', headerName: 'OU Path', width: 200, sortable: true },
      { field: 'joinedMethod', headerName: 'Joined Method', width: 100, sortable: true },
      { 
        field: 'joinedTimestamp', 
        headerName: 'Joined Date', 
        width: 120, 
        sortable: true,
        renderCell: (params) => {
          try {
            if (!params.value || params.value === '') return <span>-</span>;
            const date = new Date(params.value);
            if (isNaN(date.getTime())) return <span>Invalid Date</span>;
            return <span>{date.toLocaleDateString()}</span>;
          } catch (error) {
            console.warn('Error formatting date:', error, params.value);
            return <span>Error</span>;
          }
        }
      },
      // Always show Functional Unit column
      {
        field: 'functionalUnit',
        headerName: 'Functional Unit',
        width: 130,
        sortable: true,
        renderCell: (params) => {
          const functionalUnit = params.row?.tags?.functionalUnit || '-';
          const displayText = functionalUnit !== '-' && functionalUnit.includes('\n') ? '\n' + functionalUnit : functionalUnit;
          return <span style={{ whiteSpace: 'pre-line' }}>{displayText}</span>;
        }
      },
      // Always show Business Unit column
      {
        field: 'businessUnit',
        headerName: 'Business Unit',
        width: 130,
        sortable: true,
        renderCell: (params) => {
          const businessUnit = params.row?.tags?.businessUnit || '-';
          const displayText = businessUnit !== '-' && businessUnit.includes('\n') ? '\n' + businessUnit : businessUnit;
          return <span style={{ whiteSpace: 'pre-line' }}>{displayText}</span>;
        }
      },
    ];

    // Show Account Owner column when filter is applied
    if (accountOwnerFilter && accountOwnerFilter.trim() !== '') {
      baseColumns.push({
        field: 'accountOwner',
        headerName: 'Account Owner',
        width: 150,
        sortable: true,
        renderCell: (params) => {
          const accountOwner = params.row?.tags?.accountOwner || 'N/A';
          const displayText = accountOwner !== 'N/A' && accountOwner.includes('\n') ? '\n' + accountOwner : accountOwner;
          return <span style={{ whiteSpace: 'pre-line' }}>{displayText}</span>;
        }
      });
    }
    
    // Show Technical Contact column when filter is applied
    if (technicalContactFilter && technicalContactFilter.trim() !== '') {
      baseColumns.push({
        field: 'technicalContact',
        headerName: 'Technical Contact',
        width: 150,
        sortable: true,
        renderCell: (params) => {
          const technicalContact = params.row?.tags?.technicalContact || 'N/A';
          const displayText = technicalContact !== 'N/A' && technicalContact.includes('\n') ? '\n' + technicalContact : technicalContact;
          return <span style={{ whiteSpace: 'pre-line' }}>{displayText}</span>;
        }
      });
    }

    baseColumns.push(
      {
        field: 'actions',
        headerName: 'View',
        width: 80,
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(params.row);
            }}
          >
            <ExpandMore />
          </IconButton>
        )
      }
    );

    return baseColumns;
  }, [selectedAccounts, filteredAccounts, accountOwnerFilter, technicalContactFilter]);

  const handleSelectAccount = (account) => {
    setSelectedAccounts(prev => {
      const exists = prev.some(acc => acc.id === account.id);
      if (exists) {
        return prev.filter(acc => acc.id !== account.id);
      } else {
        return [...prev, account];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedAccounts.length === filteredAccounts.length) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts([...filteredAccounts]);
    }
  };

  const logout = () => oktaAuth.signOut();

  // Resolve active API endpoint based on selected partition
  const ACTIVE_API = partition === 'govcloud' && GOVCLOUD_API_ENDPOINT
    ? GOVCLOUD_API_ENDPOINT
    : API_ENDPOINT;

  const authenticatedFetch = async (url, options = {}) => {
    const headers = { 'Content-Type': 'application/json' };
    
    if (authState?.accessToken?.accessToken) {
      headers['Authorization'] = `Bearer ${authState.accessToken.accessToken}`;
    }
    
    return fetch(url, { ...options, headers: { ...headers, ...(options.headers || {}) } });
  };

  const loadAccountCounts = async () => {
    try {
      const response = await authenticatedFetch(`${ACTIVE_API}/accounts?counts-only=true`);
      const result = await response.json();
      console.log('[DEBUG] loadAccountCounts response:', result);
      if (result.error) throw new Error(result.error);

      // Handle cache rebuilding
      if (result.rebuilding) {
        setCopySnackbarMessage('Cache is rebuilding, please wait...');
        setCopySnackbarOpen(true);
        setTimeout(() => loadAccountCounts(), 20000);
        return;
      }
      
      if (result.counts) {
        console.log('[DEBUG] Setting counts from result.counts:', result.counts);
        setAccountCounts(result.counts);
      } else {
        // Fallback: if API doesn't support counts-only, load all and extract counts
        const accounts = result;
        console.log('[DEBUG] Fallback - calculating counts from accounts array, length:', accounts.length);
        setAccountCounts({
          total: accounts.length,
          active: accounts.filter(acc => acc.status === 'ACTIVE').length,
          suspended: accounts.filter(acc => acc.status === 'SUSPENDED').length
        });
      }
    } catch (err) {
      console.error('Error loading counts:', err.message);
    }
  };

  const loadAccounts = async (forceReload = false, searchTerm = '', limit = null) => {
    if (accountsLoaded && !forceReload && !searchTerm) return;
    
    setLoading(true);
    setError(null);
    try {
      // Build query parameters for performance optimization
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
        params.append('limit', '100'); // Limit search results
      } else if (limit) {
        params.append('limit', limit.toString());
      } else {
        // For initial load, load more accounts by default
        // Note: Removed lightweight mode to ensure OU names are populated
        params.append('limit', '500'); // Load more accounts by default
      }
      if (forceReload) params.append('refresh', 'true');
      
      const url = `${ACTIVE_API}/accounts${params.toString() ? '?' + params.toString() : ''}`;
      console.log('Loading accounts with URL:', url);
      
      const response = await authenticatedFetch(url);
      const result = await response.json();
      if (result.error) throw new Error(result.error);

      // Handle "cache rebuilding" response — show snackbar and auto-retry
      if (result.rebuilding) {
        setAllAccounts([]);
        setAccountsLoaded(false);
        setLoading(true);
        setCopySnackbarMessage('Cache is rebuilding, please wait...');
        setCopySnackbarOpen(true);
        // Auto-retry after 20 seconds
        setTimeout(() => loadAccounts(false, '', null), 20000);
        return;
      }
      
      // Handle both old and new response formats
      const accounts = Array.isArray(result) ? result : (result.accounts || []);
      const totalCount = result.total_count || accounts.length;
      
      console.log(`[DEBUG] loadAccounts response - accounts.length: ${accounts.length}, totalCount: ${totalCount}, limit: ${limit}`);
      console.log('[DEBUG] Full result:', result);
      console.log(`Loaded ${accounts.length} accounts (total: ${totalCount})`);
      if (result.performance) console.log('Performance mode:', result.performance);
      
      setAllAccounts(accounts);
      setAccountsLoaded(true);
      
      // Always update total count from backend response
      if (totalCount && totalCount > 0) {
        console.log('[DEBUG] Updating total count to:', totalCount);
        setAccountCounts(prevCounts => ({
          ...prevCounts,
          total: totalCount
        }));
      }
      
      // For full loads without search/limit, also update active/suspended counts
      if (!searchTerm && (!limit || limit >= totalCount)) {
        console.log('[DEBUG] Full load detected - updating all counts. Limit:', limit, 'TotalCount:', totalCount);
        setAccountCounts({
          total: totalCount,
          active: accounts.filter(acc => acc.status === 'ACTIVE').length,
          suspended: accounts.filter(acc => acc.status === 'SUSPENDED').length
        });
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError(`Failed to load accounts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadOUs = async () => {
    try {
      const response = await authenticatedFetch(`${ACTIVE_API}/ous`);
      const ous = await response.json();
      if (ous.error) throw new Error(ous.error);
      setAllOUs(ous);
    } catch (err) {
      console.error(`Error loading OUs: ${err.message}`);
    }
  };
  
  const loadFunctionalUnitStats = async (forceRefresh = false) => {
    setFunctionalUnitStatsLoading(true);
    setFunctionalUnitStatsError(null);
    try {
      const params = new URLSearchParams();
      if (forceRefresh) params.append('refresh', 'true');
      
      const url = `${ACTIVE_API}/accounts/stats/functional-units${params.toString() ? '?' + params.toString() : ''}`;
      const response = await authenticatedFetch(url);
      const data = await response.json();
      
      if (data.error) throw new Error(data.error);
      
      setFunctionalUnitStats(data);
    } catch (err) {
      console.error('Error loading functional unit stats:', err);
      // Don't show error if data is simply not available yet (cache rebuilding)
      if (err.message && !err.message.includes('Failed to fetch')) {
        setFunctionalUnitStatsError(err.message);
      }
    } finally {
      setFunctionalUnitStatsLoading(false);
    }
  };
  
  const handleFunctionalUnitClick = (unitName) => {
    // Apply functional unit filter when clicking on a unit
    // Use a special marker for empty functional units
    if (unitName === '(empty)') {
      setFunctionalUnitFilter('__EMPTY__');
    } else {
      setFunctionalUnitFilter(unitName);
    }
    
    // Load accounts if not already loaded
    if (!accountsLoaded) {
      loadAccounts();
    }
  };

  const loadAccountsForOU = async (ouId) => {
    if (!ouId) {
      setCurrentOU(null);  // Clear OU context when X is clicked
      setLoading(true);
      try {
        // Reload counts first to ensure cards show correct totals
        await loadAccountCounts();
        
        // Load all accounts
        const response = await authenticatedFetch(`${ACTIVE_API}/accounts?limit=1000`);
        const result = await response.json();
        if (result.error) throw new Error(result.error);
        
        const accounts = Array.isArray(result) ? result : (result.accounts || []);
        const totalCount = result.total_count || accounts.length;
        
        console.log(`[DEBUG] Cleared OU - loaded ${accounts.length} accounts (total: ${totalCount})`);
        
        // Update both allAccounts and filteredAccounts directly
        setAllAccounts(accounts);
        setFilteredAccounts(accounts);  // Set filtered to all accounts since no filters
        setAccountsLoaded(true);
        
        // Update counts
        setAccountCounts({
          total: totalCount,
          active: accounts.filter(acc => acc.status === 'ACTIVE').length,
          suspended: accounts.filter(acc => acc.status === 'SUSPENDED').length
        });
      } catch (err) {
        console.error('Error loading accounts after clearing OU:', err);
        setError(`Failed to load accounts: ${err.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${ACTIVE_API}/accounts?ou-id=${ouId}`);
      const result = await response.json();
      if (result.error) throw new Error(result.error);
      
      // Handle both old and new response formats
      const accounts = Array.isArray(result) ? result : (result.accounts || []);
      
      setAllAccounts(accounts);
      setFilteredAccounts(accounts);
      setCurrentOU(allOUs.find(ou => ou.id === ouId));
      setAccountsLoaded(true);
      
      // Update counts for the selected OU
      setAccountCounts({
        total: accounts.length,
        active: accounts.filter(acc => acc.status === 'ACTIVE').length,
        suspended: accounts.filter(acc => acc.status === 'SUSPENDED').length
      });
    } catch (err) {
      console.error('Error loading accounts for OU:', err);
      setError(`Failed to load accounts for OU: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const wildcardMatch = (text, pattern) => {
    if (!pattern || pattern.trim() === '') return true;
    if (!text || text === '') return false;
    
    const lowerText = text.toLowerCase().trim();
    const lowerPattern = pattern.toLowerCase().trim();
    
    if (lowerPattern.startsWith('*') && lowerPattern.endsWith('*')) {
      const searchTerm = lowerPattern.slice(1, -1);
      return searchTerm === '' || lowerText.includes(searchTerm);
    } else if (lowerPattern.startsWith('*')) {
      const searchTerm = lowerPattern.slice(1);
      return searchTerm === '' || lowerText.endsWith(searchTerm);
    } else if (lowerPattern.endsWith('*')) {
      const searchTerm = lowerPattern.slice(0, -1);
      return searchTerm === '' || lowerText.startsWith(searchTerm);
    }
    return lowerText.includes(lowerPattern);
  };

  const filterAccounts = () => {
    try {
      let filtered = [...allAccounts];
      
      if (searchTerm) {
        filtered = filtered.filter(account => {
          const name = account.name || '';
          const id = account.id || '';
          const email = account.email || '';
          return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 id.includes(searchTerm) ||
                 email.toLowerCase().includes(searchTerm.toLowerCase());
        });
      }
      
      if (functionalUnitFilter && functionalUnitFilter.trim() !== '') {
        if (functionalUnitFilter === '__EMPTY__') {
          // Filter for accounts without functional unit tag, excluding suspended accounts
          filtered = filtered.filter(account => 
            (!account.tags?.functionalUnit || account.tags.functionalUnit.trim() === '') &&
            account.status === 'ACTIVE'
          );
        } else {
          // Normal wildcard matching
          filtered = filtered.filter(account => 
            wildcardMatch(account.tags?.functionalUnit || '', functionalUnitFilter)
          );
        }
      }
      
      if (businessUnitFilter && businessUnitFilter.trim() !== '') {
        filtered = filtered.filter(account => 
          wildcardMatch(account.tags?.businessUnit || '', businessUnitFilter)
        );
      }
      
      if (accountOwnerFilter && accountOwnerFilter.trim() !== '') {
        filtered = filtered.filter(account => 
          wildcardMatch(account.tags?.accountOwner || '', accountOwnerFilter)
        );
      }
      
      if (technicalContactFilter && technicalContactFilter.trim() !== '') {
        filtered = filtered.filter(account => 
          wildcardMatch(account.tags?.technicalContact || '', technicalContactFilter)
        );
      }
      
      if (statusFilter) {
        filtered = filtered.filter(account => account.status === statusFilter);
      }
      
      setFilteredAccounts(filtered);

      // Update stat cards to reflect the current filtered set
      const hasActiveFilter = searchTerm || functionalUnitFilter || businessUnitFilter || accountOwnerFilter || technicalContactFilter || statusFilter;
      if (hasActiveFilter) {
        setAccountCounts({
          total: filtered.length,
          active: filtered.filter(acc => acc.status === 'ACTIVE').length,
          suspended: filtered.filter(acc => acc.status === 'SUSPENDED').length
        });
      } else {
        // No filters active — reset cards to full dataset counts
        setAccountCounts({
          total: allAccounts.length,
          active: allAccounts.filter(acc => acc.status === 'ACTIVE').length,
          suspended: allAccounts.filter(acc => acc.status === 'SUSPENDED').length
        });
      }
    } catch (error) {
      console.error('Error filtering accounts:', error);
      setError('Error filtering accounts. Please try again.');
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSelectedOU('');
    setCurrentOU(null);
    setFunctionalUnitFilter('');
    setBusinessUnitFilter('');
    setAccountOwnerFilter('');
    setTechnicalContactFilter('');
    setStatusFilter('');
    setAccountsLoaded(false);
    setAllAccounts([]);
    setFilteredAccounts([]);
    // Reload both counts and accounts to reset to full dataset
    loadAccountCounts();
    loadAccounts(false, '', 1000);
  };

  const clearCache = async () => {
    setClearCacheDialogOpen(false);
    try {
      // Step 1: clear the cache
      const response = await authenticatedFetch(`${ACTIVE_API}/clear-cache`, { method: 'DELETE' });
      const result = await response.json();
      if (result.error) throw new Error(result.error);

      // Step 2: reset UI state
      setAccountsLoaded(false);
      setAllAccounts([]);
      setFilteredAccounts([]);
      setAccountCounts({ total: 0, active: 0, suspended: 0 });

      // Step 3: trigger async background rebuild
      await authenticatedFetch(`${ACTIVE_API}/refresh-cache`, { method: 'POST' });

      // Step 4: show persistent rebuilding snackbar
      setCopySnackbarMessage('Cache is rebuilding, please wait...');
      setCopySnackbarOpen(true);
      setError(null);

      // Step 5: poll until cache is back, then reload everything
      const pollForReady = async (attempt = 0) => {
        if (attempt > 15) {
          setCopySnackbarMessage('Cache rebuild is taking longer than expected. Please reload the page.');
          return;
        }
        try {
          const res = await authenticatedFetch(`${ACTIVE_API}/accounts?counts-only=true`);
          const data = await res.json();
          if (data.counts && data.counts.total > 0) {
            // Cache is back — reload all widgets
            setCopySnackbarOpen(false);
            setAccountCounts(data.counts);
            loadAccounts(true);
            loadOUs();
            // FU stats may need a few more seconds to rebuild — retry with delay
            loadFunctionalUnitStats(true);
            setTimeout(() => loadFunctionalUnitStats(true), 15000);
            setCopySnackbarMessage('Cache rebuilt successfully');
            setCopySnackbarOpen(true);
            return;
          }
        } catch (e) { /* ignore fetch errors during rebuild */ }
        // Retry in 20 seconds
        setTimeout(() => pollForReady(attempt + 1), 20000);
      };
      // Start polling after a 30s initial delay (give the warming Lambda time to start)
      setTimeout(() => pollForReady(0), 30000);

    } catch (err) {
      setError(`Error clearing cache: ${err.message}`);
    }
  };

  useEffect(() => {
    if (authState?.isAuthenticated) {
      loadAccountCounts(); // Reload counts for active partition
      loadOUs(); // Reload OUs for active partition
      loadFunctionalUnitStats(); // Reload FU distribution for active partition
    }
  }, [authState?.isAuthenticated, partition]);

  useEffect(() => {
    const hasFilters = searchTerm || functionalUnitFilter || businessUnitFilter || accountOwnerFilter || technicalContactFilter;
    if (hasFilters && !accountsLoaded) {
      loadAccounts();
    }
  }, [searchTerm, functionalUnitFilter, businessUnitFilter, accountOwnerFilter, technicalContactFilter]);

  useEffect(() => {
    if (accountsLoaded) {
      filterAccounts();
    }
  }, [allAccounts, searchTerm, functionalUnitFilter, businessUnitFilter, accountOwnerFilter, technicalContactFilter, statusFilter, accountsLoaded]);

  useEffect(() => {
    // Deleted accounts only available in commercial partition (commercial HOV-BU table only)
    if (!isAdmin || !authState?.isAuthenticated || !authState?.accessToken?.accessToken) return;
    if (partition === 'govcloud') {
      setDeletedAccountsCount(null);
      setDeletedAccounts(null);
      setShowDeletedView(false);
      return;
    }
    setDeletedAccountsLoading(true);
    authenticatedFetch(`${ACTIVE_API}/accounts/deleted`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        setDeletedAccounts(data.deleted_accounts || []);
        setDeletedAccountsCount(data.count ?? 0);
      })
      .catch((err) => {
        console.error('[DELETED-ACCOUNTS] fetch error:', err);
        setDeletedAccountsCount('—');
      })
      .finally(() => setDeletedAccountsLoading(false));
  }, [isAdmin, authState?.isAuthenticated, authState?.accessToken?.accessToken, partition]);

  const handleCardClick = async (cardType) => {
    setShowDeletedView(false); // always clear deleted view when clicking other cards
    if (!accountsLoaded) {
      await loadAccounts(false, '', 1000);
    }
    if (statusFilter === cardType) {
      setStatusFilter('');
    } else {
      setStatusFilter(cardType);
    }
  };

  const handleDeletedCardClick = () => {
    if (showDeletedView) {
      setShowDeletedView(false);
    } else {
      setStatusFilter(''); // clear any active/suspended filter
      setShowDeletedView(true);
    }
  };

  // Decommissioned rows after the decom-state filter, search, and sort.
  // Shared by the result count and the DataGrid so they can never disagree.
  const visibleDeletedAccounts = useMemo(() => {
    if (!deletedAccounts) return [];
    const q = searchTerm.trim();
    const qLower = q.toLowerCase();
    return deletedAccounts
      .filter(r => decomFilter === 'ALL' || getDecomState(r) === decomFilter)
      .filter(r => !q
        || (r.account_name || '').toLowerCase().includes(qLower)
        || (r.account_id || '').includes(q))
      .sort((a, b) => {
        const da = a.decom_timestamp && a.decom_timestamp !== 'None' ? a.decom_timestamp : '';
        const db = b.decom_timestamp && b.decom_timestamp !== 'None' ? b.decom_timestamp : '';
        return db.localeCompare(da);
      })
      .map((r, i) => ({ ...r, id: r.account_id || i }));
  }, [deletedAccounts, decomFilter, searchTerm]);

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <img 
                src="/verisk_logo_white.svg" 
                alt="Verisk Logo" 
                style={{ height: '32px', marginRight: '12px' }}
              />
              <Box sx={{ borderLeft: '1px solid rgba(255, 255, 255, 0.3)', height: '24px', mr: 1 }} />
              <Typography 
                variant="h6" 
                noWrap 
                component="div"
                sx={{ fontWeight: 400, letterSpacing: '0.5px' }}
              >
                Orion Portal
              </Typography>
              {API_ENDPOINT.includes('vnhzzdkdyl') && (
                <Chip 
                  label="TEST" 
                  size="small" 
                  color="warning" 
                  sx={{ fontWeight: 'bold' }}
                />
              )}
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', px: 2 }}>
              {/* Partition toggle — Commercial / GovCloud */}
              <ToggleButtonGroup
                value={partition}
                exclusive
                size="small"
                onChange={(_, val) => {
                  if (!val) return; // prevent deselection
                  if (val === 'govcloud' && !isGovCloudReader) {
                    setGovCloudDeniedOpen(true);
                    return;
                  }
                  if (val === 'govcloud' && !GOVCLOUD_API_ENDPOINT) {
                    setGovCloudDeniedOpen(true);
                    return;
                  }
                  setPartition(val);
                  // Reset account list when switching partitions
                  setAllAccounts([]);
                  setFilteredAccounts([]);
                  setAccountsLoaded(false);
                  setAccountCounts({ total: 0, active: 0, suspended: 0 });
                  setSelectedOU('');
                  setCurrentOU(null);
                  setSearchTerm('');
                }}
                sx={{
                  mr: 2,
                  '& .MuiToggleButton-root': {
                    color: 'rgba(255,255,255,0.7)',
                    borderColor: 'rgba(255,255,255,0.3)',
                    fontSize: '0.75rem',
                    py: 0.4,
                    px: 1.5,
                    textTransform: 'none',
                    '&.Mui-selected': {
                      color: '#fff',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                    },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  },
                }}
              >
                <ToggleButton value="commercial">Commercial</ToggleButton>
                <ToggleButton value="govcloud">GovCloud</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                placeholder={showDeletedView ? "Search deleted accounts..." : "Search accounts..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                }}
                sx={{
                  width: 400,
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                }}
              />
            </Box>
            {isAdmin && (
              <Button
                color="inherit"
                startIcon={<Apps />}
                onClick={() => setAppDrawerOpen(true)}
                sx={{ ml: 2, mr: 2, textTransform: 'none' }}
              >
                Services
              </Button>
            )}
            <Tooltip title={
              !accountsLoaded || filteredAccounts.length === 0 
                ? "Load accounts to enable export" 
                : selectedAccounts.length > 0
                  ? `Export ${selectedAccounts.length} selected accounts to CSV (includes approvers)`
                  : `Export ${filteredAccounts.length} displayed accounts to CSV (includes approvers)`
            }>
              <span>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<FileDownload />}
                  onClick={() => exportToCSV(false)}
                  disabled={!accountsLoaded || filteredAccounts.length === 0}
                  sx={{ ml: 2, mr: 2 }}
                  size="small"
                >
                  Export CSV ({selectedAccounts.length > 0 ? selectedAccounts.length : filteredAccounts.length})
                </Button>
              </span>
            </Tooltip>
            {canEdit && (
              <Tooltip title={
                !accountsLoaded || filteredAccounts.length === 0 
                  ? "Load accounts to enable tag editor" 
                  : selectedAccounts.length > 0
                    ? `Edit tags for ${selectedAccounts.length} selected accounts`
                    : `Edit tags for ${filteredAccounts.length} filtered accounts`
              }>
                <span>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<Edit />}
                    onClick={() => setTagsManagementOpen(true)}
                    disabled={!accountsLoaded || filteredAccounts.length === 0}
                    sx={{ mr: 2 }}
                    size="small"
                  >
                    {selectedAccounts.length > 0 
                      ? `Tag Editor (${selectedAccounts.length} selected)` 
                      : `Tag Editor (${filteredAccounts.length})`
                    }
                  </Button>
                </span>
              </Tooltip>
            )}
            {isAdmin && (
              <Tooltip title={
                !accountsLoaded || filteredAccounts.length === 0
                  ? "Load accounts to enable approvers editor"
                  : selectedAccounts.length > 0
                    ? `Edit approvers for ${selectedAccounts.length} selected accounts`
                    : `Edit approvers for ${filteredAccounts.length} filtered accounts`
              }>
                <span>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<Security />}
                    onClick={() => setBulkApproversOpen(true)}
                    disabled={!accountsLoaded || filteredAccounts.length === 0}
                    sx={{ mr: 2 }}
                    size="small"
                  >
                    {selectedAccounts.length > 0
                      ? `Approvers Editor (${selectedAccounts.length} selected)`
                      : `Approvers Editor (${filteredAccounts.length})`
                    }
                  </Button>
                </span>
              </Tooltip>
            )}
            {isAdmin && (
              <Tooltip title="Clear cache (Admin)">
                <IconButton color="inherit" onClick={() => setClearCacheDialogOpen(true)} sx={{ mr: 1 }}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="body2" sx={{ mr: 2 }}>
              {authState?.idToken?.claims?.name || authState?.idToken?.claims?.preferred_username || authState?.idToken?.claims?.email}
            </Typography>
            <Tooltip title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton color="inherit" onClick={toggleDarkMode} sx={{ mr: 1 }}>
                {darkMode ? <LightMode /> : <DarkMode />}
              </IconButton>
            </Tooltip>
            <IconButton color="inherit" onClick={logout}>
              <Logout />
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar />
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList /> Filters
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={allOUs}
                getOptionLabel={(option) => option.fullPath || `${option.name} (${option.type})`}
                value={allOUs.find(ou => ou.id === selectedOU) || null}
                onChange={(event, newValue) => {
                  const ouId = newValue ? newValue.id : '';
                  setSelectedOU(ouId);
                  loadAccountsForOU(ouId);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organizational Unit"
                    placeholder="Select OU..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <AccountTree sx={{ mr: 1, color: 'action.active' }} />,
                    }}
                    InputLabelProps={{
                      sx: { fontWeight: 500 }
                    }}
                  />
                )}
              />
            </Box>
            
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Functional Unit"
                placeholder="e.g., EES* or UW"
                value={functionalUnitFilter}
                onChange={(e) => setFunctionalUnitFilter(e.target.value)}
                InputLabelProps={{
                  sx: { fontWeight: 500 }
                }}
                helperText="Supports wildcards (e.g., EES*, *UW*)"
              />
            </Box>
            
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Business Unit"
                placeholder="e.g., CDO, Claims"
                value={businessUnitFilter}
                onChange={(e) => setBusinessUnitFilter(e.target.value)}
                InputLabelProps={{
                  sx: { fontWeight: 500 }
                }}
                helperText="Supports wildcards (e.g., *Claims, CDO*)"
              />
            </Box>
            
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Account Owner"
                placeholder="e.g., John*"
                value={accountOwnerFilter}
                onChange={(e) => setAccountOwnerFilter(e.target.value)}
                InputLabelProps={{
                  sx: { fontWeight: 500 }
                }}
                helperText="Supports wildcards (e.g., John*, *Smith)"
              />
            </Box>
            
            <Box sx={{ mb: 2.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Technical Contact"
                placeholder="e.g., *@verisk.com"
                value={technicalContactFilter}
                onChange={(e) => setTechnicalContactFilter(e.target.value)}
                InputLabelProps={{
                  sx: { fontWeight: 500 }
                }}
                helperText="Supports wildcards (e.g., *@verisk.com)"
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<Close />}
              onClick={clearSearch}
              size="small"
              fullWidth
              disabled={!searchTerm && !selectedOU && !functionalUnitFilter && !businessUnitFilter && !accountOwnerFilter && !technicalContactFilter && !statusFilter}
              sx={{
                bgcolor: 'grey.700',
                color: 'white',
                '&:hover': { bgcolor: 'grey.600' },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'text.disabled',
                },
              }}
            >
              Clear All Filters
            </Button>
            
            {/* Functional Unit Distribution Widget */}
            <Box sx={{ mt: 3 }}>
              <FunctionalUnitDistribution
                functionalUnits={functionalUnitStats.functional_units || []}
                totalAccounts={functionalUnitStats.total_accounts || 0}
                loading={functionalUnitStatsLoading}
                error={functionalUnitStatsError}
                onUnitClick={handleFunctionalUnitClick}
              />
            </Box>
          </Box>
        </Drawer>
        
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Toolbar />
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={partition === 'govcloud' ? 4 : 3}>
              <Card 
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { boxShadow: 4 },
                  border: statusFilter === '' && accountsLoaded ? 2 : 0,
                  borderColor: 'primary.main'
                }}
                onClick={() => handleCardClick('')}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Accounts{currentOU ? ` (${currentOU.name})` : ''}
                  </Typography>
                  <Typography variant="h4" component="div">
                    {accountCounts.total}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={partition === 'govcloud' ? 4 : 3}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 },
                  border: statusFilter === 'ACTIVE' ? 2 : 0,
                  borderColor: 'success.main'
                }}
                onClick={() => handleCardClick('ACTIVE')}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Accounts{currentOU ? ` (${currentOU.name})` : ''}
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {accountCounts.active}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={partition === 'govcloud' ? 4 : 3}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 },
                  border: statusFilter === 'SUSPENDED' ? 2 : 0,
                  borderColor: 'error.main'
                }}
                onClick={() => handleCardClick('SUSPENDED')}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Suspended Accounts{currentOU ? ` (${currentOU.name})` : ''}
                  </Typography>
                  <Typography variant="h4" component="div" color="error.main">
                    {accountCounts.suspended}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {isAdmin && partition === 'commercial' && (
              <Grid item xs={12} sm={3}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 },
                    border: showDeletedView ? 2 : 0,
                    borderColor: 'error.main'
                  }}
                  onClick={handleDeletedCardClick}
                >
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Decommissioned Accounts
                    </Typography>
                    <Typography variant="h4" component="div" color="error.main">
                      {deletedAccountsLoading ? <CircularProgress size={28} /> : (deletedAccountsCount ?? '—')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error: {error}
            </Alert>
          )}

          {/* Deleted Accounts inline view */}
          {!loading && showDeletedView && (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" color="error">
                  Decommissioned Accounts
                </Typography>
                {deletedAccounts && (
                  <Typography variant="body2" color="text.secondary">
                    {visibleDeletedAccounts.length === deletedAccounts.length
                      ? `${deletedAccounts.length} results`
                      : `${visibleDeletedAccounts.length} of ${deletedAccounts.length} results`}
                  </Typography>
                )}
              </Box>

              {/* Decommission state filter */}
              {deletedAccounts && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  {DECOM_FILTERS.map(key => {
                    const isAll = key === 'ALL';
                    const meta = DECOM_STATES[key];
                    const n = isAll
                      ? deletedAccounts.length
                      : deletedAccounts.filter(r => getDecomState(r) === key).length;
                    const selected = decomFilter === key;
                    return (
                      <Chip
                        key={key}
                        label={`${isAll ? 'All' : meta.label} (${n})`}
                        size="small"
                        onClick={() => setDecomFilter(key)}
                        color={selected ? (isAll ? 'default' : meta.color) : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.72rem', fontWeight: selected ? 700 : 400 }}
                      />
                    );
                  })}
                  <Tooltip title="Accounts with a decommission request that AWS has not finished removing yet — they remain SUSPENDED during AWS's closure grace period.">
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, cursor: 'help', textDecoration: 'underline dotted' }}>
                      What is "Pending Deletion"?
                    </Typography>
                  </Tooltip>
                </Box>
              )}
              {deletedAccountsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!deletedAccountsLoading && deletedAccounts && (
                <ErrorBoundary>
                  <Box sx={{ height: 'calc(100vh - 300px)', minHeight: 400, width: '100%' }}>
                    <DataGrid
                      rows={visibleDeletedAccounts}
                      columns={[
                        {
                          field: 'decom_timestamp', headerName: 'Decommission Date', width: 180,
                          renderCell: (p) => {
                            const v = p.value;
                            if (!v || v === 'None') return <span>N/A</span>;
                            try {
                              let d;
                              if (v.includes('T')) {
                                d = new Date(v);
                              } else {
                                const parts = v.split('-');
                                if (parts.length < 3) return <span>{v}</span>;
                                const p1 = +parts[1], p2 = +parts[2];
                                const mon = p1 > 12 ? p2 : p1;
                                const day = p1 > 12 ? p1 : p2;
                                d = new Date(+parts[0], mon - 1, day, +(parts[3]||0), +(parts[4]||0));
                              }
                              if (isNaN(d.getTime())) return <span>{v}</span>;
                              return <span>{d.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' })} {d.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' })}</span>;
                            } catch { return <span>{v}</span>; }
                          }
                        },
                        { field: 'account_name', headerName: 'Account Name', flex: 1, minWidth: 160 },
                        { field: 'account_id', headerName: 'Account ID', width: 130 },
                        {
                          field: 'decom_state', headerName: 'Decom Status', width: 150,
                          valueGetter: (value, row) => getDecomState(row),
                          renderCell: (p) => {
                            const meta = DECOM_STATES[p.value] || DECOM_STATES.PENDING_DELETION;
                            return <Chip label={meta.label} color={meta.color} size="small" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700 }} />;
                          }
                        },
                        {
                          field: 'org_status', headerName: 'AWS Status', width: 120,
                          // Prefer the real Organizations status; the attributes
                          // table column lags and can read SUSPENDED long after
                          // AWS has removed the account.
                          valueGetter: (value, row) => row?.org_status || row?.account_status || '',
                          renderCell: (p) => {
                            const v = p.value;
                            if (!v || v === 'None') return <span>N/A</span>;
                            const label = v === 'REMOVED' ? 'Removed' : v;
                            const color = v === 'REMOVED' ? 'text.disabled' : 'text.primary';
                            return (
                              <Typography component="span" sx={{ fontSize: '0.78rem', color }}>
                                {label}
                              </Typography>
                            );
                          }
                        },
                        { field: 'business_unit_alignment', headerName: 'Functional Unit', width: 130,
                          renderCell: (p) => <span>{p.value && p.value !== 'None' ? p.value : 'N/A'}</span> },
                        { field: 'business_unit', headerName: 'Business Unit', width: 130,
                          renderCell: (p) => <span>{p.value && p.value !== 'None' ? p.value : 'N/A'}</span> },
                        { field: 'account_env', headerName: 'Environment', width: 100,
                          renderCell: (p) => <span>{p.value && p.value !== 'None' ? p.value : 'N/A'}</span> },
                        { field: 'account_type', headerName: 'Account Type', width: 120,
                          renderCell: (p) => <span>{p.value && p.value !== 'None' ? p.value : 'N/A'}</span> },
                        { field: 'decom_request_id', headerName: 'Decom Request ID', width: 150,
                          renderCell: (p) => <span>{p.value && p.value !== 'None' ? p.value : 'N/A'}</span> },
                      ]}
                      pageSize={25}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      disableSelectionOnClick
                      density="compact"
                      onRowClick={(params) => setDeletedAccountDetail(params.row)}
                      sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-cell:hover': { color: 'error.main' },
                      }}
                    />
                  </Box>
                </ErrorBoundary>
              )}
            </Box>
          )}

          {/* Normal accounts view */}
          {!loading && !showDeletedView && !error && !accountsLoaded && (
            <Alert severity="info" sx={{ textAlign: 'center' }}>
              Click a card above or apply filters to view accounts
            </Alert>
          )}
          
          {!loading && !showDeletedView && !error && accountsLoaded && filteredAccounts.length === 0 && (
            <Alert severity="info">
              No accounts found matching your search.
            </Alert>
          )}
          
          {!loading && !showDeletedView && !error && accountsLoaded && filteredAccounts.length > 0 && (
            <Box sx={{ width: '100%' }}>
              {/* Selection Info Bar */}
              {selectedAccounts.length > 0 && (
                <Box sx={{ 
                  mb: 2, 
                  p: 2, 
                  bgcolor: 'primary.light', 
                  color: 'primary.contrastText',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="body1">
                    {selectedAccounts.length} account{selectedAccounts.length !== 1 ? 's' : ''} selected
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => setSelectedAccounts([])}
                    sx={{ color: 'inherit', borderColor: 'inherit' }}
                  >
                    Clear Selection
                  </Button>
                </Box>
              )}
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" color="primary">
                  Accounts ({filteredAccounts.length} results)
                </Typography>
              </Box>
              <ErrorBoundary>
                <Box sx={{ height: 'calc(100vh - 300px)', minHeight: 400, width: '100%' }}>
                  <DataGrid
                    rows={filteredAccounts}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    disableSelectionOnClick
                    density="compact"
                    onRowClick={(params) => handleRowClick(params.row)}
                    onError={(error) => {
                      console.error('DataGrid error:', error);
                      setError('Error displaying data. Please try again.');
                    }}
                    sx={{
                      '& .MuiDataGrid-cell:hover': {
                        color: 'primary.main',
                      },
                      '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                      },
                    }}
                  />
                </Box>
              </ErrorBoundary>
            </Box>
          )}
          

        </Box>
        
        {/* App Drawer for Services */}
        <Drawer
          anchor="right"
          open={appDrawerOpen}
          onClose={() => setAppDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 400,
              p: 3,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" color="primary">
              Cloud Services
            </Typography>
            <IconButton onClick={() => setAppDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: 2,
                  borderColor: 'primary.main',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountBalance sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Account Explorer</Typography>
                      <Chip label="Current" size="small" color="primary" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    View and manage AWS Organizations accounts with advanced filtering
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => { setAppDrawerOpen(false); alert('Account Factory - Coming Soon'); }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CloudQueue sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Account Factory</Typography>
                      <Chip label="Coming Soon" size="small" color="warning" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Automated provisioning of new AWS accounts with standardized configurations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => { setAppDrawerOpen(false); alert('Cloud Governance - Coming Soon'); }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Security sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Cloud Governance</Typography>
                      <Chip label="Coming Soon" size="small" color="warning" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Enforce policies, compliance rules, and security standards across accounts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => { setAppDrawerOpen(false); alert('Cost Analytics - Coming Soon'); }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Assessment sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Cost Analytics</Typography>
                      <Chip label="Coming Soon" size="small" color="warning" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Track spending, analyze costs, and optimize cloud resource usage
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Drawer>
        
        {/* Clear Cache Confirmation Dialog */}
        <Dialog
          open={clearCacheDialogOpen}
          onClose={() => setClearCacheDialogOpen(false)}
        >
          <DialogTitle>Clear Cache?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will clear all cached account and OU data. The data will be refreshed from AWS Organizations on the next load.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClearCacheDialogOpen(false)}>Cancel</Button>
            <Button onClick={clearCache} color="error" variant="contained">
              Clear Cache
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Copy Feedback Snackbar */}
        <Snackbar
          open={copySnackbarOpen}
          autoHideDuration={3000}
          onClose={() => setCopySnackbarOpen(false)}
          message={copySnackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />

        {/* GovCloud access denied Snackbar */}
        <Snackbar
          open={govCloudDeniedOpen}
          autoHideDuration={4000}
          onClose={() => setGovCloudDeniedOpen(false)}
          message={
            !GOVCLOUD_API_ENDPOINT
              ? "GovCloud integration not configured. Contact your administrator."
              : "You don't have access to GovCloud accounts. Contact your administrator."
          }
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
        
        {/* Tags Management Dialog - Bulk Update */}
        <TagsManagement
          open={tagsManagementOpen}
          onClose={() => {
            setTagsManagementOpen(false);
            setSelectedAccounts([]); // Clear selection when closing
          }}
          accounts={selectedAccounts.length > 0 ? selectedAccounts : filteredAccounts}
          onUpdate={() => {
            // Refresh all accounts and FU stats after bulk update
            loadAccounts(true);
            loadFunctionalUnitStats(true);
            setSelectedAccounts([]); // Clear selection after update
          }}
          authenticatedFetch={authenticatedFetch}
          API_ENDPOINT={ACTIVE_API}
        />

        {/* Bulk Approvers Management Dialog */}
        <BulkApproversManagement
          open={bulkApproversOpen}
          onClose={() => { setBulkApproversOpen(false); setSelectedAccounts([]); }}
          accounts={selectedAccounts.length > 0 ? selectedAccounts : filteredAccounts}
          authenticatedFetch={authenticatedFetch}
          API_ENDPOINT={ACTIVE_API}
          onComplete={() => { loadAccounts(true); setSelectedAccounts([]); }}
        />

        {/* Deleted Account Detail Dialog — raw DynamoDB record */}
        {deletedAccountDetail && (
          <Dialog
            open={!!deletedAccountDetail}
            onClose={() => setDeletedAccountDetail(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  {deletedAccountDetail.account_name || deletedAccountDetail.account_id || 'Account Record'}
                </Typography>
                {deletedAccountDetail.account_status && (
                  <Chip
                    label={deletedAccountDetail.account_status}
                    color={deletedAccountDetail.account_status === 'DIVESTITURE' ? 'warning' : 'error'}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20, fontWeight: 700 }}
                  />
                )}
              </Box>
              <IconButton size="small" onClick={() => setDeletedAccountDetail(null)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              <Box
                component="pre"
                sx={{
                  m: 0, p: 2,
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  bgcolor: darkMode ? 'grey.900' : 'grey.50',
                  color: 'text.primary',
                  maxHeight: '70vh',
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(
                  // Remove the synthetic DataGrid 'id' field before displaying
                  Object.fromEntries(Object.entries(deletedAccountDetail).filter(([k]) => k !== 'id')),
                  null,
                  2
                )}
              </Box>
            </DialogContent>
          </Dialog>
        )}

        {/* Session Timeout Warning */}
        <SessionTimeoutWarning
          open={showSessionWarning}
          timeRemaining={timeRemaining}
          onExtendSession={extendSession}
          onLogout={sessionLogout}
        />
      </Box>
      
      {/* Version Footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          p: 1,
          bgcolor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
          borderTopLeftRadius: 8,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          v{APP_VERSION}
        </Typography>
      </Box>
    </>
  );
};

function App() {
  const { authState, oktaAuth } = useOktaAuth();
  const [loading, setLoading] = useState(true);

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('orion-dark-mode');
    if (saved !== null) return saved === 'true';
    return getSystemDarkMode();
  });

  // Partition state — 'commercial' or 'govcloud'. Resets to commercial on page refresh.
  const [partition, setPartition] = useState('commercial');

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('orion-dark-mode', String(next));
      return next;
    });
  };

  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#1976d2' },
      secondary: { main: '#dc004e' },
    },
    typography: {
      h4: { fontWeight: 600 },
    },
  }), [darkMode]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Checking authentication state...');
        if (oktaAuth.isLoginRedirect()) {
          console.log('Handling login redirect...');
          await oktaAuth.handleLoginRedirect();
        }
        const isAuthenticated = await oktaAuth.isAuthenticated();
        console.log('Is authenticated:', isAuthenticated);
        setLoading(false);
      } catch (error) {
        console.error('Auth check error:', error);
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [oktaAuth]);

  if (loading) {
    return (
      <ColorModeContext.Provider value={{ darkMode, toggleDarkMode, partition, setPartition }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <CircularProgress />
          </Box>
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }

  if (authState?.error) {
    return (
      <ColorModeContext.Provider value={{ darkMode, toggleDarkMode, partition, setPartition }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Alert severity="error">
              <Typography variant="h6">Authentication Error</Typography>
              <Typography>{authState.error.message}</Typography>
              <Button onClick={() => oktaAuth.signInWithRedirect()} sx={{ mt: 2 }}>
                Try Again
              </Button>
            </Alert>
          </Container>
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }

  console.log('App authState:', authState);
  
  return (
    <ColorModeContext.Provider value={{ darkMode, toggleDarkMode, partition, setPartition }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={{
          '*::-webkit-scrollbar': { width: '8px', height: '8px' },
          '*::-webkit-scrollbar-track': {
            background: darkMode ? '#1e1e1e' : '#f1f1f1',
          },
          '*::-webkit-scrollbar-thumb': {
            background: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: darkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)',
          },
        }} />
        <Routes>
          <Route path="/login/callback" element={<LoginCallback />} />
          <Route path="/accounts/:accountId" element={authState?.isAuthenticated ? <AccountDetailPage /> : <Login />} />
          <Route path="/*" element={authState?.isAuthenticated ? <Dashboard /> : <Login />} />
        </Routes>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;

